import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { createCharacterAnimator, type AnimInput, type CharacterAnimator, type FootstepEvent } from './animation';
import { styleKey, type CharacterStyle } from './characterStyle';
import { figureLook, footShape, GeoBuilder, handShape, HAT_KINDS, hairCap, hatShape, headBase, headFaces, Limb, limbShape, TAIL_DEPTH, TAIL_HEM, tailShape, Tone, torsoBase, torsoCut, type Detail, type FigureLook, type Sink } from './figure';
import { createSkeleton, Joint, JOINT_COUNT, JOINT_NAMES, JOINT_PARENTS, POSE_HIPS_OFFSET, type Pose, type Proportions, type Skeleton } from './skeleton';
import { villainArm, villainCap, villainFoot, villainHand, villainHead, villainLeg, villainSleeve, villainTail, villainTorso, villainWeights } from './villainFigure';

/**
 * Character mesh built on the shared skeleton from the shared figure pieces (figure.ts): faces with
 * brows, sockets, eyes, nose and lips; ten hair styles; facial hair; hands with fingers and a thumb;
 * shoes with soles and heels; collars, lapels, ties, pockets and buttons per cut. Colours are baked
 * into vertex colours, so a figure is one mesh per joint (≈17 draw calls) with one shared material.
 * Geometry is cached per look and ref-counted, so many rigs with the same look share GPU resources.
 */

/** One merged part: vertex-coloured geometry in joint-local space. */
export interface CharacterPart {
  joint: number;
  geometry: THREE.BufferGeometry;
}

export interface CharacterRigOptions {
  /** Uniform visual scale (the player is drawn ~1.22× for readability from the high camera). */
  scale?: number;
  castShadow?: boolean;
  /** Seed for idle variation of the built-in animator. */
  seed?: number;
}

export interface CharacterRig {
  readonly root: THREE.Group;
  /** Joint transforms, indexed by `Joint`. */
  readonly joints: readonly THREE.Object3D[];
  readonly skeleton: Skeleton;
  readonly animator: CharacterAnimator;
  readonly style: CharacterStyle;
  /** Footstep hook (forwarded to the animator). */
  onFootstep: ((e: FootstepEvent) => void) | null;
  /** Write a pose onto the joint transforms. */
  applyPose(pose: Pose): void;
  /** Convenience: advance the built-in animator and apply its pose. */
  update(input: AnimInput): Pose;
  /** Swap clothing/colours/build in place (keeps the root, its parent and the animator hook). */
  setStyle(style: CharacterStyle): void;
  /**
   * Hide the rig's own body meshes while the joints keep animating (kept across setStyle). The detailed
   * player test (detailedPlayer.ts) draws a different model driven by these joints.
   */
  setBodyVisible(visible: boolean): void;
  /** Where things held in the right hand hang (a weapon); null = `joints[Joint.handR]`. */
  handRSocket: THREE.Object3D | null;
  dispose(): void;
}

// --- part geometry ------------------------------------------------------------------------------

const lin = (hex: number): THREE.Color => new THREE.Color(hex);
const cyl = (rt: number, rb: number, h: number, seg = 8): THREE.BufferGeometry => new THREE.CylinderGeometry(rt, rb, h, seg, 1);

/**
 * Build the merged, vertex-coloured part list for a style. Pure geometry (no materials, no caching).
 * The caller owns the returned geometries.
 */
export function createCharacterParts(style: CharacterStyle, dims: Proportions, detail: Detail = 'high'): CharacterPart[] {
  const d = dims;
  const look: FigureLook = figureLook(style, d);
  const p = style.palette;
  const perJoint = new Map<number, THREE.BufferGeometry[]>();
  const push = (j: number, g: THREE.BufferGeometry): void => {
    let list = perJoint.get(j);
    if (!list) perJoint.set(j, (list = []));
    list.push(g);
  };
  /** Run `fill` against fresh per-joint builders and bake them with `channels`. */
  const kind = (channels: readonly number[], fill: (sink: Sink) => void, blend?: readonly number[], hair = false, post?: (g: THREE.BufferGeometry) => void): void => {
    const builders = new Map<number, GeoBuilder>();
    const sink: Sink = (j) => {
      let b = builders.get(j);
      if (!b) builders.set(j, (b = new GeoBuilder(hair)));
      return b;
    };
    fill(sink);
    const cols = channels.map(lin);
    for (const [j, b] of builders) {
      const g = b.bake(cols, blend);
      post?.(g);
      push(j, g);
    }
  };

  const h = d.height;
  const w = d.width;
  const L = d.limb;
  const villain = style.sculpt === 'villain';
  const tailored = villain && style.outfit === 'longCoat';

  kind(look.torso, (sink) => {
    if (tailored) villainTorso(sink, d, detail);
    else {
      torsoBase(sink, d, detail);
      torsoCut(sink, look.cut, d, detail);
    }
  });
  // Pelvis (trousers). The top stays inside the spine's depth (0.74 × waist) at any chest twist, so
  // it can't poke through the waist in a swing or an aim.
  kind([p.trousers], (sink) => {
    sink(Joint.hips).add(cyl(d.waistRadius * 0.7 * d.depth, d.hipHalf + 0.085 * L, 0.2 * h, 10).scale(1, 1, 0.75 * d.depth).translate(0, 0.02 * h, 0), Tone.A, 0.95);
  });
  // Coat skirt / skirt / seat from the shared unit shape.
  const hem = d.waistRadius * TAIL_HEM;
  if (tailored) kind([look.tailColour], (sink) => villainTail(sink(Joint.coatTail), d, detail));
  else kind(
    [look.tailColour],
    (sink) => tailShape(sink(Joint.coatTail), look.tailKind, detail),
    undefined,
    false,
    (g) => {
      g.scale(hem, look.tail, hem * TAIL_DEPTH * d.depth);
      g.translate(0, -0.02 * h, 0);
    },
  );
  // Arms: shaped sleeves with a round elbow (coat, or a shirt rolled up over a bare forearm). Cuffs
  // are trims. The rig is seen close, so its limbs get twelve sides.
  const SIDES = detail === 'high' ? 12 : 8;
  kind([p.coat, p.shirt, p.skin], (sink) => {
    for (const [ua, la] of [
      [Joint.upperArmL, Joint.lowerArmL],
      [Joint.upperArmR, Joint.lowerArmR],
    ] as const) {
      if (tailored) {
        villainArm(sink(ua), d, detail);
        villainSleeve(sink(la), d, detail);
      } else if (look.shirtArms) {
        limbShape(sink(ua), Limb.upperArm, detail, 0.066 * L, d.upperArm, SIDES, Tone.B);
        sink(la).add(cyl(0.066 * L, 0.064 * L, 0.06 * h).translate(0, -0.03 * h, 0), Tone.B, 0.92);
        limbShape(sink(la), Limb.bareForearm, detail, 0.058 * L, d.lowerArm, SIDES, Tone.C);
      } else {
        limbShape(sink(ua), Limb.upperArm, detail, 0.07 * L, d.upperArm, SIDES, Tone.A);
        limbShape(sink(la), Limb.forearm, detail, 0.062 * L, d.lowerArm, SIDES, Tone.A);
      }
    }
  });
  // Legs: shaped thighs and calves, trousers with a 70s flare and turn-up; skirts show tights.
  kind([p.trousers], (sink) => {
    const k = look.skirt ? 0.82 : 1;
    for (const [ul, ll] of [
      [Joint.upperLegL, Joint.lowerLegL],
      [Joint.upperLegR, Joint.lowerLegR],
    ] as const) {
      if (villain && !look.skirt) {
        villainLeg(sink(ul), d, detail);
      } else {
        limbShape(sink(ul), Limb.thigh, detail, 0.088 * w * k, d.upperLeg + 0.02, SIDES);
        limbShape(sink(ll), look.skirt ? Limb.shinTights : Limb.shin, detail, 0.068 * w * k, d.lowerLeg, SIDES);
      }
    }
  });
  kind(look.head, (sink) => {
    if (villain) villainHead(sink, d, detail, style);
    else {
      headBase(sink, d, detail);
      headFaces(sink, [look.face], d, detail);
    }
  });
  if (look.hair) kind([p.hair], (sink) => hairCap(sink(Joint.head), d, detail), look.hair, true, villain && style.headwear === 'flatCap' ? (g) => {
    // The authored cap sits lower at the forehead; tuck the existing style's crown underneath it.
    const pos = g.getAttribute('position');
    const r = d.headRadius; const hc = r * 0.85;
    for (let i = 0; i < pos.count; i++) {
      const x = pos.getX(i); const z = pos.getZ(i);
      let y = Math.min(pos.getY(i), hc + r * 0.64 - z * 0.15 + x * 0.065);
      if (style.hair === 'sidePart' || style.hair === 'short') y = Math.max(y, hc - r * 0.57);
      pos.setXYZ(i, x * 0.97, y, z * 0.97);
    }
    g.computeVertexNormals();
  } : undefined);
  if (look.hat >= 0) kind(look.hatColours, (sink) => {
    if (villain && style.headwear === 'flatCap') villainCap(sink(Joint.head), d, detail);
    else hatShape(sink(Joint.head), HAT_KINDS[look.hat]!, d, detail);
  });
  kind([p.skin], (sink) => {
    const hand = villain ? villainHand : handShape;
    hand(sink(Joint.handL), 1, d, detail);
    hand(sink(Joint.handR), -1, d, detail);
  });
  kind([p.shoes], (sink) => {
    const k = style.shoesKind ?? 'shoes';
    for (const j of [Joint.footL, Joint.footR]) {
      if (villain && k === 'shoes') villainFoot(sink(j), d, detail);
      else footShape(sink(j), k, d, detail);
    }
  });
  for (const t of look.trims) {
    if (tailored && (t.joint === Joint.lowerArmL || t.joint === Joint.lowerArmR)) continue;
    kind([t.colour], (sink) => {
      sink(t.joint).add(new THREE.BoxGeometry(t.sx, t.sy, t.sz).translate(t.x, t.y, t.z), Tone.A, 1, (_x, y) => (y > t.sy * 0.45 ? 1.08 : 1));
    });
  }

  const parts: CharacterPart[] = [];
  for (const [joint, list] of perJoint) {
    const geometry = list.length === 1 ? list[0]! : (mergeGeometries(list) ?? list[0]!);
    for (const g of list) if (g !== geometry) g.dispose();
    if (villain && joint === Joint.head) geometry.scale(1.1, 1.1, 1.1);
    if (villain && (joint === Joint.footL || joint === Joint.footR)) geometry.scale(0.95, 1, 0.94);
    if (villain) villainWeights(geometry, joint, d, tailored);
    geometry.computeBoundingSphere();
    parts.push({ joint, geometry });
  }
  return parts;
}

// --- shared resource caches ---------------------------------------------------------------------

interface Cached<T> {
  value: T;
  refs: number;
}
const partCache = new Map<string, Cached<CharacterPart[]>>();
let material: Cached<THREE.MeshStandardMaterial> | null = null;

function acquireParts(style: CharacterStyle, skeleton: Skeleton): CharacterPart[] {
  const k = styleKey(style);
  let c = partCache.get(k);
  if (!c) partCache.set(k, (c = { value: createCharacterParts(style, skeleton.dims), refs: 0 }));
  c.refs++;
  return c.value;
}

function releaseParts(style: CharacterStyle): void {
  const k = styleKey(style);
  const c = partCache.get(k);
  if (!c || --c.refs > 0) return;
  for (const p of c.value) p.geometry.dispose();
  partCache.delete(k);
}

function acquireMaterial(): THREE.MeshStandardMaterial {
  if (!material) material = { value: new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.8, metalness: 0.02 }), refs: 0 };
  material.refs++;
  return material.value;
}

function releaseMaterial(): void {
  if (!material || --material.refs > 0) return;
  material.value.dispose();
  material = null;
}

/** For tests/diagnostics: live cache sizes. */
export function characterCacheStats(): { parts: number; materials: number } {
  return { parts: partCache.size, materials: material ? 1 : 0 };
}

// --- rig ----------------------------------------------------------------------------------------

/**
 * Create a posable character. No dependency on game services: the player view, pedestrians and
 * remote players can all own one. Call `update(input)` each frame (or `applyPose` with a pose from
 * your own animator).
 */
export function createCharacterRig(initial: CharacterStyle, opts: CharacterRigOptions = {}): CharacterRig {
  const root = new THREE.Group();
  root.name = 'character';
  root.scale.setScalar(opts.scale ?? 1);
  const castShadow = opts.castShadow ?? true;
  // Bone is an Object3D with the identical transforms/hierarchy used by the shared animator.
  // Only the opted-in player geometry uses skinning; other rigs remain ordinary rigid meshes.
  const joints: THREE.Bone[] = [];
  for (let j = 0; j < JOINT_COUNT; j++) {
    const o = new THREE.Bone();
    o.name = JOINT_NAMES[j]!;
    joints.push(o);
    const parent = JOINT_PARENTS[j]!;
    (parent < 0 ? root : joints[parent]!).add(o);
  }

  let style = initial;
  let skeleton = createSkeleton(style.build, style.height, style.sculpt);
  const traits = (s: CharacterStyle) => ({
    age: s.age ?? 0.3,
    female: s.female ?? (s.outfit === 'dressSkirt' || s.outfit === 'miniSkirt'),
    smoker: !!s.smoker,
    swagger: s.swagger ?? 0,
    bulk: s.build === 'heavy' ? 1 : s.build === 'stocky' ? 0.45 : 0,
  });
  let animator = createCharacterAnimator({ build: style.build, height: style.height, scale: opts.scale ?? 1, seed: opts.seed, sculpt: style.sculpt });
  animator.setTraits(traits(style));
  let footstep: ((e: FootstepEvent) => void) | null = null;
  let meshes: THREE.Mesh[] = [];
  let skin: THREE.Skeleton | null = null;
  let bodyVisible = true;

  function placeRest(): void {
    const rest = skeleton.rest;
    for (let j = 0; j < JOINT_COUNT; j++) joints[j]!.position.set(rest[j * 3]!, rest[j * 3 + 1]!, rest[j * 3 + 2]!);
  }

  function build(): void {
    placeRest();
    // A style swap may happen in mid-animation; bind against rest, then restore the pose below.
    for (const joint of joints) joint.rotation.set(0, 0, 0);
    root.updateWorldMatrix(true, true);
    const parts = acquireParts(style, skeleton);
    const mat = acquireMaterial();
    if (parts.some(p => p.geometry.hasAttribute('skinWeight'))) skin = new THREE.Skeleton(joints);
    for (const part of parts) {
      const m = skin && part.geometry.hasAttribute('skinWeight') ? new THREE.SkinnedMesh(part.geometry, mat) : new THREE.Mesh(part.geometry, mat);
      m.castShadow = castShadow;
      m.visible = bodyVisible;
      m.name = `${JOINT_NAMES[part.joint]}`;
      joints[part.joint]!.add(m);
      if (m instanceof THREE.SkinnedMesh && skin) {
        m.updateWorldMatrix(true, false);
        m.bind(skin, m.matrixWorld);
        // Conservative joint-local envelope for all authored poses, avoiding per-frame CPU
        // bounds recomputation for the GPU-deformed cloth.
        m.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 2.5 * skeleton.dims.height);
      }
      meshes.push(m);
    }
  }

  function teardown(): void {
    for (const m of meshes) m.removeFromParent();
    meshes = [];
    skin?.dispose();
    skin = null;
    releaseMaterial();
    releaseParts(style);
  }

  function applyPose(pose: Pose): void {
    const rest = skeleton.rest;
    for (let j = 0; j < JOINT_COUNT; j++) joints[j]!.rotation.set(pose[j * 3]!, pose[j * 3 + 1]!, pose[j * 3 + 2]!);
    joints[Joint.hips]!.position.set(rest[0]! + pose[POSE_HIPS_OFFSET]!, rest[1]! + pose[POSE_HIPS_OFFSET + 1]!, rest[2]! + pose[POSE_HIPS_OFFSET + 2]!);
  }

  build();
  applyPose(animator.pose);

  const rig: CharacterRig = {
    root,
    joints,
    get skeleton() {
      return skeleton;
    },
    get animator() {
      return animator;
    },
    get style() {
      return style;
    },
    get onFootstep() {
      return footstep;
    },
    set onFootstep(fn) {
      footstep = fn;
      animator.onFootstep = fn;
    },
    applyPose,
    update(input) {
      const pose = animator.update(input);
      applyPose(pose);
      return pose;
    },
    setStyle(next) {
      if (styleKey(next) === styleKey(style)) return;
      const rebuildSkeleton = next.build !== style.build || next.height !== style.height || next.sculpt !== style.sculpt;
      teardown();
      style = next;
      if (rebuildSkeleton) {
        skeleton = createSkeleton(style.build, style.height, style.sculpt);
        animator = createCharacterAnimator({ build: style.build, height: style.height, scale: opts.scale ?? 1, seed: opts.seed, sculpt: style.sculpt });
        animator.onFootstep = footstep;
      }
      animator.setTraits(traits(style));
      build();
      applyPose(animator.pose);
    },
    setBodyVisible(visible) {
      bodyVisible = visible;
      for (const m of meshes) m.visible = visible;
    },
    handRSocket: null,
    dispose() {
      teardown();
      root.removeFromParent();
    },
  };
  return rig;
}
